import { EyeIcon, Users, X } from "lucide-react";
import { useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";

type BusinessScoreProps = {
  brandValueDisplay: string;
  totalRegisteredProfiles: number;
  brandValueProgressWidth: string;
};

const BusinessScore = ({
  brandValueDisplay,
  totalRegisteredProfiles,
  brandValueProgressWidth,
}: BusinessScoreProps) => {
  const [showBusinessInfo, setShowBusinessInfo] = useState(false);
  const { t } = useLanguage();
  return (
    <div>
      <section className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-violet-700 via-purple-600 to-pink-500 px-6 py-4 shadow-[0_20px_50px_rgba(109,40,217,0.28)]">
        <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute bottom-0 left-0 h-32 w-32 rounded-full bg-white/10 blur-xl" />

        <div className="relative">
          <div className="flex items-start justify-between gap-3">
            {/* Left side */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="whitespace-nowrap text-lg font-semibold text-white">
                  {t("Business Score")}
                </h2>

                <button
                  type="button"
                  aria-label={t("View Business Score Information")}
                  onClick={() => setShowBusinessInfo(true)}
                  className="shrink-0 rounded-full bg-white/15 p-1.5 transition hover:bg-white/25"
                >
                  <EyeIcon size={16} className="text-white" />
                </button>
              </div>

              <h1 className="mt-3 whitespace-nowrap text-2xl font-bold leading-none text-white">
                {brandValueDisplay}%
              </h1>
            </div>

            {/* Right side */}
            <div className="flex w-[135px] shrink-0 flex-col items-center text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
                <Users size={20} className="text-white" />
              </div>

              <span className="mt-1.5 text-2xl font-bold leading-none text-white">
                {totalRegisteredProfiles}
              </span>

              <span className="mt-1 text-[11px] leading-4 text-white/80">
                {t("Total Registered Profiles")}
              </span>
            </div>
          </div>

          {/* Progress */}
          <div className="mt-4">
            <div className="h-3 overflow-hidden rounded-full bg-white/20">
              <div
                className="h-full rounded-full bg-gradient-to-r from-pink-400 to-pink-200 transition-all duration-500"
                style={{ width: brandValueProgressWidth }}
              />
            </div>
          </div>

          <p className="mt-4 text-sm leading-6 text-white/90">
            {t(
              "Keep it up! You're doing great. A few more actions can take you to the next level.",
            )}
          </p>
        </div>
      </section>

      {showBusinessInfo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Business Score</h2>

              <button
                onClick={() => setShowBusinessInfo(false)}
                className="rounded-full bg-gray-100 p-2 hover:bg-gray-200"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3 text-sm leading-7 text-gray-600">
              <p>
                📌 Every profile with <b>80%+</b> completion increases your
                Business Score.
              </p>

              <p>📌 More completed profiles improve your bureau ranking.</p>

              <p>📌 Higher Business Score increases customer trust.</p>

              <p>
                📌 Complete profile details, photos and contact information to
                improve the score.
              </p>
            </div>

            <button
              onClick={() => setShowBusinessInfo(false)}
              className="mt-6 w-full rounded-xl bg-violet-600 py-3 font-semibold text-white"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessScore;
